import React, { useState, useEffect } from 'react';
import { PlayCircle, BookOpen, Search, Filter, RefreshCw, AlertCircle, Sparkles, Key } from 'lucide-react';
import useUnitCode from '../hooks/useUnitCode';
import UnitCard from '../components/unit/UnitCard';
import { MyUnitCodeDto } from '../types/unit';
import { useNavigate, Link } from 'react-router-dom';
import UnitCodePopup from '../components/common/UnitCodePopup';

const MyLectures: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { 
    units, 
    isLoading, 
    error, 
    totalCount, 
    totalPages, 
    currentPage,
    getMyUnits,
    clearError,
    refreshUnits
  } = useUnitCode();

  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUnits, setFilteredUnits] = useState<MyUnitCodeDto[]>([]);
  const [isUnitCodePopupOpen, setIsUnitCodePopupOpen] = useState(false);

  // Filter units based on search only
  useEffect(() => {
    let filtered = units;

    // Search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(unit => 
        unit.unitName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        unit.unitTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (unit.courseName && unit.courseName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredUnits(filtered);
  }, [units, searchTerm]);

  const handlePageChange = (page: number) => {
    getMyUnits(page);
  };

  const handleRefresh = () => {
    clearError();
    refreshUnits();
  };

  const handleUnitCodeSuccess = () => {
    // Refresh units after successful code entry
    refreshUnits();
    setIsUnitCodePopupOpen(false);
  };

  const handleUnitClick = (unitId: number) => {
    navigate(`/unit-viewer/${unitId}`);
  };

  const getGradeInArabic = (grade: string) => {
    const gradeTranslations: Record<string, string> = {
      'Secondary1': 'أولى ثانوي',
      'Secondary2': 'ثانية ثانوي',
      'Secondary3': 'ثالثة ثانوي',
      'default': grade
    };
    return gradeTranslations[grade] || grade;
  };

  const getTermInArabic = (term: string) => {
    const terms: Record<string, string> = {
      'First': 'الترم الأول',
      'Second': 'الترم الثاني',
      'default': term
    };
    return terms[term] || term;
  };

  // Loading shimmer component
  const UnitCardShimmer = () => (
    <div className="bg-white/80 backdrop-blur-xl rounded-[1.5rem] border border-white/30 shadow-lg overflow-hidden animate-pulse">
      <div className="p-6 bg-gradient-to-br from-gray-100 to-gray-200">
        <div className="w-16 h-16 mx-auto bg-gray-300 rounded-2xl mb-4"></div>
      </div>
      <div className="p-6">
        <div className="h-6 bg-gray-300 rounded mb-2"></div>
        <div className="h-4 bg-gray-200 rounded mb-4"></div>
        <div className="h-10 bg-gray-200 rounded mb-6"></div>
        <div className="h-10 bg-gray-300 rounded"></div>
      </div>
    </div>
  );

  // Empty state component
  const EmptyState = () => (
    <div className="text-center py-16">
      <div className="inline-block p-6 bg-gradient-to-br from-orange-100 to-orange-200 rounded-full mb-6">
        <PlayCircle className="w-16 h-16 text-orange-600" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-4">لا توجد حصص بعد</h3>
      <p className="text-gray-600 max-w-md mx-auto leading-relaxed mb-6">
        لم تشترك في أي حصص حتى الآن. تصفّح الحصص المتاحة واشترك للوصول إلى المحتوى التعليمي.
      </p>
      <Link
        to="/student-portal/browse"
        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all"
      >
        <BookOpen className="w-4 h-4" />
        تصفّح الحصص والكورسات
      </Link>
    </div>
  );

  // Error state component
  const ErrorState = () => (
    <div className="text-center py-16">
      {/* <div className="inline-block p-6 bg-red-100 rounded-full mb-6">
        <AlertCircle className="w-16 h-16 text-red-600" />
      </div> */}
      <h3 className="text-2xl font-bold text-gray-900 mb-4">لا توجد حصص مسجّلة</h3>
      <p className="text-gray-600 mb-6">{error}</p>
      <button
        onClick={handleRefresh}
        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
      >
        <RefreshCw className="w-4 h-4" />
        إعادة المحاولة
      </button>
    </div>
  );

  return (
    <div className={embedded ? '' : 'min-h-screen bg-white'} dir="rtl">
      <div className={`mx-auto px-4 sm:px-6 lg:px-8 ${embedded ? 'max-w-4xl py-2' : 'max-w-7xl py-8'}`}>
        {/* Header Section */}
        {!embedded && (
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-green-100 to-gold-100 px-6 py-3 rounded-full border border-green-200/50 mb-6">
            <PlayCircle className="w-5 h-5 text-green-700" />
            <span className="text-green-800 font-medium">حصصي</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-6 leading-tight">
            <span className="inline-flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-green-700 rounded-2xl flex items-center justify-center shadow-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="text-gray-800">حصصي</span>
            </span>
          </h1>
          
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            الحصص اللي اشتركت فيها — ادخل وكمّل تعلمك
          </p>
        </div>
        )}

        {/* Search and Actions */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-green-100 shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="البحث في حصصك..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
              />
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              {/* Enter Unit Code Button */}
              <button
                onClick={() => setIsUnitCodePopupOpen(true)}
                className="px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-xl hover:shadow-lg transition-all duration-300 flex items-center gap-2"
              >
                <Key className="w-5 h-5" />
                <span className="hidden sm:inline">إدخال كود محاضرة</span>
                <span className="sm:hidden">كود</span>
              </button>
              
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="px-4 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-xl hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        {!isLoading && !error && (
          <div className="text-center mb-8">
            <p className="text-gray-600">
              {filteredUnits.length > 0 ? (
                <>
                  عرض <span className="font-semibold text-orange-600">{filteredUnits.length}</span> من أصل{' '}
                  <span className="font-semibold text-orange-600">{totalCount}</span> وحدة
                </>
              ) : searchTerm ? (
                'لا توجد نتائج مطابقة للبحث'
              ) : (
                'لا توجد وحدات متاحة'
              )}
            </p>
          </div>
        )}

        {/* Content */}
        {error ? (
          <ErrorState />
        ) : isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 6 }).map((_, index) => (
              <UnitCardShimmer key={`shimmer-${index}`} />
            ))}
          </div>
        ) : filteredUnits.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredUnits.map((unit) => (
              <div
                key={unit.unitId}
                onClick={() => handleUnitClick(unit.unitId)}
                className="cursor-pointer"
              >
                <UnitCard unit={unit} />
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && !error && totalPages > 1 && (
          <div className="mt-12 mb-2 flex flex-wrap items-center justify-center gap-2 pb-16 sm:pb-0">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg bg-white text-gray-600 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 border border-gray-200"
            >
              السابق
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                    currentPage === page
                      ? 'bg-gradient-to-r from-orange-600 to-orange-700 text-white shadow-lg'
                      : 'bg-white text-gray-600 hover:bg-orange-50 hover:text-orange-600 border border-gray-200'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-lg bg-white text-gray-600 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 border border-gray-200"
            >
              التالي
            </button>
          </div>
        )}
      </div>

      {/* Unit Code Popup */}
      <UnitCodePopup 
        isOpen={isUnitCodePopupOpen} 
        onClose={() => setIsUnitCodePopupOpen(false)}
        onSuccess={handleUnitCodeSuccess}
      />
    </div>
  );
};

export default MyLectures; 